/**
 * The Agents timeline model: every subagent and workflow run across sessions, as lanes
 * grouped by session, clipped to a time window. A workflow run carries its agents, each a
 * lane of its own. Pure functions, unit-testable.
 */
import type { Activity, Id, RunStatus, Session, Ts, WorkflowAgent, WorkflowPhase, WorkflowRun } from '@orgus/protocol';
import { agentStatus } from '../../lib/running.ts';

export type WindowKey = '1h' | '6h' | '24h';
export type RunFilter = 'all' | 'running' | 'failed';

const MIN = 60_000;
const H = 60 * MIN;

export const WINDOWS: Record<WindowKey, { ms: number; step: number; label: string; long: string }> = {
  '1h': { ms: H, step: 10 * MIN, label: '1h', long: 'the last hour' },
  '6h': { ms: 6 * H, step: H, label: '6h', long: 'the last 6 hours' },
  '24h': { ms: 24 * H, step: 3 * H, label: '24h', long: 'the last 24 hours' },
};

export const WINDOW_KEYS: WindowKey[] = ['1h', '6h', '24h'];

export function isWindowKey(v: unknown): v is WindowKey {
  return v === '1h' || v === '6h' || v === '24h';
}

export interface TimelineRun {
  kind: 'subagent' | 'workflow' | 'agent';
  id: Id;
  sessionId: Id;
  /** What it is doing: the subagent's description or the workflow's description. */
  title: string;
  /** The subagent type or the workflow name. */
  label: string;
  status: RunStatus;
  startedAt: Ts;
  endedAt: Ts | null;
  toolCalls: number | null;
  lastText: string;
  phases: WorkflowPhase[] | null;
  /** A workflow's agents (kind 'agent'), in start order; null for anything else. On a lane, the ones it lists. */
  agents: TimelineRun[] | null;
  /**
   * Every agent of the workflow, set on a lane whose filter or window listed fewer: its
   * progress, bar and counts stay the whole workflow's (see workflowAgents).
   */
  allAgents?: TimelineRun[];
  /** A workflow's agents the machine did not list (more than it reports per run). */
  agentsOmitted: number;
  /** An agent's phase title. */
  phase: string | null;
  /** An agent's git worktree. */
  worktree: string | null;
  /** An agent's workflow run. */
  workflowId: Id | null;
  /** An agent's attempt at its label (2 and up: a re-run of a resumed or retried workflow). */
  attempt?: number;
  /** Set on an agent a later attempt replaced: the id of that later agent. */
  supersededBy?: Id;
  /** When a subagent or an agent actually worked (null when its machine does not report it). */
  activity: Activity | null;
}

export interface Lane {
  session: Session;
  runs: TimelineRun[];
  running: number;
}

const NO_EXTRA = { agents: null, agentsOmitted: 0, phase: null, worktree: null, workflowId: null, activity: null } as const;

function agentRun(s: Session, w: WorkflowRun, a: WorkflowAgent): TimelineRun {
  return {
    kind: 'agent',
    id: a.id,
    sessionId: s.id,
    title: a.label,
    label: a.label,
    status: agentStatus(w, a),
    startedAt: a.startedAt,
    endedAt: a.endedAt,
    toolCalls: a.toolCalls,
    lastText: a.lastText,
    phases: null,
    agents: null,
    agentsOmitted: 0,
    phase: a.phase,
    worktree: a.worktree,
    workflowId: w.id,
    ...(a.attempt !== undefined ? { attempt: a.attempt } : {}),
    ...(a.supersededBy !== undefined ? { supersededBy: a.supersededBy } : {}),
    activity: a.activity ?? null,
  };
}

/** How an agent's attempt reads (its icon's tooltip, the drawer's Attempt fact): 'earlier attempt', 'attempt 2', or nothing. */
export function attemptNote(a: Pick<TimelineRun, 'attempt' | 'supersededBy'>): string | null {
  if (a.supersededBy) return 'earlier attempt';
  return a.attempt && a.attempt > 1 ? `attempt ${a.attempt}` : null;
}

export function runsOf(s: Session): TimelineRun[] {
  const subs = s.subagents.map(
    (a): TimelineRun => ({
      ...NO_EXTRA,
      kind: 'subagent',
      id: a.id,
      sessionId: s.id,
      title: a.description || a.type,
      label: a.type,
      status: a.status,
      startedAt: a.startedAt,
      endedAt: a.endedAt,
      toolCalls: a.toolCalls,
      lastText: a.lastText,
      phases: null,
      activity: a.activity ?? null,
    }),
  );
  const flows = s.workflows.map(
    (w): TimelineRun => ({
      ...NO_EXTRA,
      // Machines on older versions do not report agents: treat that as none listed.
      agents: (w.agents ?? []).map((a) => agentRun(s, w, a)).sort((x, y) => x.startedAt - y.startedAt || x.label.localeCompare(y.label)),
      agentsOmitted: w.agentsOmitted ?? 0,
      kind: 'workflow',
      id: w.id,
      sessionId: s.id,
      title: w.description || w.name,
      label: w.name,
      status: w.status,
      startedAt: w.startedAt,
      endedAt: w.endedAt,
      toolCalls: w.agents?.length ? w.agents.reduce((n, a) => n + a.toolCalls, 0) : null,
      lastText: '',
      phases: w.phases,
    }),
  );
  return [...subs, ...flows].sort((a, b) => a.startedAt - b.startedAt || a.id.localeCompare(b.id));
}

function passes(r: TimelineRun, f: RunFilter): boolean {
  if (f === 'running') return r.status === 'running';
  if (f === 'failed') return r.status === 'failed';
  return true;
}

/** In the window. A running run always is, even when its machine's clock runs ahead of ours. */
function overlaps(r: TimelineRun, windowStart: Ts, now: Ts): boolean {
  return r.status === 'running' || ((r.endedAt ?? now) >= windowStart && r.startedAt <= now);
}

/**
 * What a run counts as: a subagent is one, a workflow is its agents (or one, when its
 * machine lists none). A workflow of 13 agents at work is 13 running.
 */
export function unitsOf(r: TimelineRun): TimelineRun[] {
  return r.kind === 'workflow' && r.agents?.length ? r.agents : [r];
}

/** A workflow's every agent, whatever its lane lists: what its progress and its bar are made of. */
export function workflowAgents(r: Pick<TimelineRun, 'agents' | 'allAgents'>): TimelineRun[] {
  return r.allAgents ?? r.agents ?? [];
}

/**
 * Runs that overlap [windowStart, now], grouped by session; sessions with live runs first,
 * then newest. A workflow keeps the agents that pass the filter and overlap the window,
 * and shows when any of them does (or, with no agents listed, when it passes itself).
 */
export function buildLanes(sessions: Session[], windowStart: Ts, now: Ts, filter: RunFilter = 'all'): Lane[] {
  const lanes: Lane[] = [];
  for (const s of sessions) {
    const runs: TimelineRun[] = [];
    for (const r of runsOf(s)) {
      if (r.kind === 'workflow' && r.agents?.length) {
        const agents = r.agents.filter((a) => overlaps(a, windowStart, now) && passes(a, filter));
        if (agents.length) runs.push(agents.length === r.agents.length ? r : { ...r, agents, allAgents: r.agents });
      } else if (overlaps(r, windowStart, now) && passes(r, filter)) runs.push(r);
    }
    if (!runs.length) continue;
    lanes.push({ session: s, runs, running: runs.flatMap(unitsOf).filter((r) => r.status === 'running').length });
  }
  // Recency from the runs alone, never from `now`: `now` ticks once a second behind the live
  // data, so a run that just ended (endedAt a moment past `now`) ranked above every open run for
  // up to a second, its whole lane jumped to the top and back. An open run counts as newest.
  const latest = (l: Lane) => Math.max(...l.runs.map((r) => r.endedAt ?? Number.POSITIVE_INFINITY));
  const byLatest = (a: Lane, b: Lane) => {
    const la = latest(a);
    const lb = latest(b);
    return la === lb ? 0 : lb > la ? 1 : -1;
  };
  return lanes.sort((a, b) => Number(b.running > 0) - Number(a.running > 0) || byLatest(a, b) || a.session.id.localeCompare(b.session.id));
}

export interface RunCounts {
  all: number;
  running: number;
  failed: number;
}

/**
 * Subagents and workflow agents in the window (see unitsOf), by status. `running` is the same
 * number as the nav badge (lib/running.ts runningUnits), whatever the window.
 */
export function countRuns(sessions: Session[], windowStart: Ts, now: Ts): RunCounts {
  const c: RunCounts = { all: 0, running: 0, failed: 0 };
  for (const s of sessions) {
    for (const r of runsOf(s).flatMap(unitsOf)) {
      if (!overlaps(r, windowStart, now)) continue;
      c.all++;
      if (r.status === 'running') c.running++;
      if (r.status === 'failed') c.failed++;
    }
  }
  return c;
}

export interface PhaseGroup {
  title: string;
  agents: TimelineRun[];
  running: number;
  done: number;
  failed: number;
  startedAt: Ts;
  /** null while any agent of the phase runs. */
  endedAt: Ts | null;
}

/** A workflow's agents grouped by phase, phases in the run's order (then first seen). */
export function phaseGroups(run: Pick<TimelineRun, 'phases' | 'agents'>): PhaseGroup[] {
  const order = (run.phases ?? []).map((p) => p.title);
  const by = new Map<string, TimelineRun[]>();
  for (const a of run.agents ?? []) {
    const k = a.phase ?? '';
    const list = by.get(k) ?? [];
    list.push(a);
    by.set(k, list);
    if (!order.includes(k)) order.push(k);
  }
  const out: PhaseGroup[] = [];
  for (const title of order) {
    const agents = by.get(title);
    if (!agents?.length) continue;
    const live = agents.some((a) => a.endedAt == null);
    out.push({
      title,
      agents,
      running: agents.filter((a) => a.status === 'running').length,
      done: agents.filter((a) => a.status === 'done').length,
      failed: agents.filter((a) => a.status === 'failed').length,
      startedAt: Math.min(...agents.map((a) => a.startedAt)),
      endedAt: live ? null : Math.max(...agents.map((a) => a.endedAt ?? a.startedAt)),
    });
  }
  return out;
}

/** Tick times inside [start, end], aligned to local clock multiples of `step`. */
export function ticksFor(start: Ts, end: Ts, step: number): Ts[] {
  const off = new Date(start).getTimezoneOffset() * MIN;
  const first = Math.ceil((start - off) / step) * step + off;
  const out: Ts[] = [];
  for (let t = first; t <= end; t += step) out.push(t);
  return out;
}

/** Fractions of the window covered by a run: left edge, width, and whether it began before the window. */
export function placement(r: Pick<TimelineRun, 'startedAt' | 'endedAt'>, start: Ts, end: Ts): { left: number; width: number; clipped: boolean } {
  const span = Math.max(1, end - start);
  const l = Math.max(0, Math.min(1, (r.startedAt - start) / span));
  const rgt = Math.max(0, Math.min(1, ((r.endedAt ?? end) - start) / span));
  return { left: l, width: Math.max(0, rgt - l), clipped: r.startedAt < start };
}

/** Find a run anywhere by id: a subagent, a workflow, or one agent of a workflow. */
export function findRun(sessions: Session[], id: Id): TimelineRun | undefined {
  for (const s of sessions) {
    for (const r of runsOf(s)) {
      if (r.id === id) return r;
      const a = r.agents?.find((x) => x.id === id);
      if (a) return a;
    }
  }
  return undefined;
}

/** Phase progress as a 0..1 fraction of finished agents. */
export function workflowProgress(phases: WorkflowPhase[]): number {
  const total = phases.reduce((n, p) => n + Math.max(0, p.agents), 0);
  const done = phases.reduce((n, p) => n + Math.min(Math.max(0, p.done), Math.max(0, p.agents)), 0);
  return total ? done / total : 0;
}

/** The `?project=` value for sessions without a project. */
export const NO_PROJECT = 'none';

/** A session's project as a filter key: its id, or NO_PROJECT. */
export function projectKey(s: Pick<Session, 'projectId'>): string {
  return s.projectId ?? NO_PROJECT;
}

export interface ProjectGroup {
  /** projectKey of its sessions. */
  key: string;
  lanes: Lane[];
  /** Subagents and workflow agents running in its lanes (see unitsOf). */
  running: number;
}

/**
 * Lanes grouped by project: projects with running work first, then in the order their newest
 * lane comes (buildLanes already sorts lanes live first, then newest). Lanes keep their order.
 */
/**
 * Sessions a project shows in the timeline before the rest fold behind "Show N more". Every lane
 * of every project drew 650 sessions (30,000 elements with the live room) at scale and froze the
 * page as it opened (21 Oct 2026).
 */
export const PROJECT_LANES = 10;

/** The lanes a project shows: the first `limit`, plus the one holding the open run when it is further down. */
export function lanesShown(lanes: Lane[], all: boolean, activeRunId: string | null, limit = PROJECT_LANES): Lane[] {
  if (all || lanes.length <= limit) return lanes;
  const head = lanes.slice(0, limit);
  if (!activeRunId) return head;
  const i = lanes.findIndex((l) => l.runs.some((r) => r.id === activeRunId || r.agents?.some((a) => a.id === activeRunId)));
  return i >= limit ? [...head, lanes[i]!] : head;
}

export function groupByProject(lanes: Lane[]): ProjectGroup[] {
  const by = new Map<string, ProjectGroup>();
  for (const l of lanes) {
    const key = projectKey(l.session);
    const g = by.get(key) ?? { key, lanes: [], running: 0 };
    g.lanes.push(l);
    g.running += l.running;
    by.set(key, g);
  }
  // A stable sort: among equals the first-seen (newest) order stays.
  return [...by.values()].sort((a, b) => Number(b.running > 0) - Number(a.running > 0));
}
